import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const assortCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	memoryCacheTTL: 30 * 60 * 1000,
	logCategory: 'Assort',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.assort.`,
});

export async function getAssortSegments(forceRefresh = false) {
	return assortCache.fetchOrCache('segments', fetchAssortSegmentsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortProducts(forceRefresh = false) {
	if (!forceRefresh && !assortCache.getFromMemory('products')) {
		try {
			const legacyData = await iframeDataBridge.getStorageAsync('SFA_ASSORTPRODUCTS');
			if (legacyData) {
				log('info', 'Assort', '📦 Seeding Vue cache from legacy storage (SFA_ASSORTPRODUCTS)');
				await assortCache.put('products', legacyData);
				assortCache.setInMemory('products', legacyData);
				return legacyData;
			}
		} catch (legacyError) {
			log('warn', 'Assort', `⚠️ Legacy storage check failed: ${legacyError.message}`);
		}
	}

	return assortCache.fetchOrCache('products', fetchAssortProductsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortCatalogs(forceRefresh = false) {
	return assortCache.fetchOrCache('catalogs', fetchAssortCatalogsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortProductContainers(forceRefresh = false) {
	return assortCache.fetchOrCache('productContainers', fetchAssortProductContainersFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortProductOrderContainers(forceRefresh = false) {
	return assortCache.fetchOrCache('productOrderContainers', fetchAssortProductOrderContainersFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortCompetitors(forceRefresh = false) {
	return assortCache.fetchOrCache('competitors', fetchAssortCompetitorsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortProductAccounts(forceRefresh = false) {
	return assortCache.fetchOrCache('productAccounts', fetchAssortProductAccountsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getAssortUserAgreement(forceRefresh = false) {
	return assortCache.fetchOrCache('userAgreement', fetchAssortUserAgreementFromAPI, { forceRefresh, fallback: null, backgroundRefresh: true });
}

async function fetchAssortSegmentsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/Segment'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const segments = await response.json();
	log('success', 'Assort', `✅ Fetched ${segments.length} assort segments`);
	return segments;
}

async function fetchAssortProductsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/Products'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const products = await response.json();
	log('success', 'Assort', `✅ Fetched ${products.length} assort products`);
	return products;
}

async function fetchAssortCatalogsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/Catalogs'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const catalogs = await response.json();
	log('success', 'Assort', `✅ Fetched ${catalogs.length} assort catalogs`);
	return catalogs;
}

async function fetchAssortProductContainersFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/ProductAssortContainers'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const productContainers = await response.json();
	log('success', 'Assort', `✅ Fetched ${productContainers.length} assort product containers`);
	return productContainers;
}

async function fetchAssortProductOrderContainersFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/ProductOrderContainers'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const productOrderContainers = await response.json();
	log('success', 'Assort', `✅ Fetched ${productOrderContainers.length} assort product order containers`);
	return productOrderContainers;
}

async function fetchAssortCompetitorsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/Competitors'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const competitors = await response.json();
	log('success', 'Assort', `✅ Fetched ${competitors.length} assort competitors`);
	return competitors;
}

async function fetchAssortProductAccountsFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/ProductAccounts'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const productAccounts = await response.json();
	log('success', 'Assort', `✅ Fetched ${productAccounts.length} assort product accounts`);
	return productAccounts;
}

async function fetchAssortUserAgreementFromAPI() {
	const response = await authenticatedFetch(getApiUrl('Assort/UserAgreement'), { method: 'GET' }, 'tokenSales');
	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	const userAgreement = await response.json();
	log('success', 'Assort', '✅ Fetched assort user agreement');
	return userAgreement;
}

export async function refreshAssort() {
	assortCache.clearMemory();
	await assortCache.clear();
}
